import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Compass, 
  Users, 
  Heart, 
  MessageCircle, 
  BookOpen, 
  Handshake,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const services = [
  {
    icon: Compass,
    title: "Behavioral Health System Navigation",
    description: "We guide individuals through the complex landscape of behavioral health services, helping them understand their options and find the right resources for their specific needs."
  },
  {
    icon: Users,
    title: "Family Support Guidance",
    description: "Families often feel overwhelmed when supporting loved ones with behavioral health challenges. We provide guidance to help families understand available resources and navigate the support process."
  },
  {
    icon: Heart,
    title: "Community Wellness Check-ins",
    description: "Regular wellness check-ins help us stay connected with community members, identify emerging needs, and ensure ongoing access to appropriate support resources."
  },
  {
    icon: MessageCircle,
    title: "Connection to Services",
    description: "We connect individuals with behavioral health services that match their needs, facilitating warm handoffs to ensure smooth transitions and continued care."
  },
  {
    icon: BookOpen,
    title: "Resource Education",
    description: "Through educational initiatives, we help community members understand behavioral health concepts, available services, and how to access support when needed."
  },
  {
    icon: Handshake,
    title: "Community Partnership Collaboration",
    description: "We work with community organizations, healthcare providers, and social service agencies to strengthen the overall behavioral health support network."
  },
];

const WhatWeDo = () => {
  return (
    <div className="min-h-screen" data-testid="what-we-do-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
              What We Do
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Behavioral Health Navigation & Workforce Agency provides non-clinical behavioral health navigation services designed to help individuals and families understand and access available resources.
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
              Bridging the Gap to Care
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We focus on supporting individuals who may be overwhelmed by complex healthcare systems by offering guidance, education, and connection to appropriate services. Our services emphasize navigation, connection, and community engagement, helping individuals move from confusion and uncertainty toward informed access to care.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-100 hover:border-brand-teal"
                  data-testid={`service-card-${index}`}
                >
                  <CardContent className="p-0">
                    <div className="w-14 h-14 bg-brand-teal/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon size={28} className="text-brand-teal" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-blue mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-brand-neutral">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white p-8 rounded-2xl border-l-4 border-brand-teal">
            <CardContent className="p-0">
              <h3 className="font-heading text-xl font-bold text-brand-blue mb-4">
                Important Note
              </h3>
              <p className="text-gray-600 leading-relaxed">
                These services do not replace clinical care but instead help individuals and families navigate the systems that provide it. We work alongside healthcare providers and community organizations to ensure comprehensive support for those we serve.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
                Our Approach
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                We believe in a person-centered approach that respects individual needs and circumstances. Our navigation services are designed to be accessible, compassionate, and effective.
              </p>
              <ul className="space-y-4">
                {[
                  "Meet individuals where they are in their journey",
                  "Provide clear, understandable information",
                  "Maintain ongoing support throughout the process",
                  "Connect with appropriate community resources",
                  "Respect privacy and confidentiality"
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-brand-teal mt-0.5 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-brand-teal to-brand-blue p-8 rounded-2xl text-white">
              <h3 className="font-heading text-2xl font-bold mb-4">
                Need Navigation Assistance?
              </h3>
              <p className="text-white/90 mb-6">
                If you or someone you know needs help navigating behavioral health resources, we're here to help guide you through the process.
              </p>
              <Link to="/request-support">
                <Button className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-6 py-3">
                  Request Support
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeDo;
